import { IFederationDto } from 'src/app/core/models/federation-dto';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import {
  IResponseDto,
  ResponseDto,
  ResponseWithErrorDto,
} from '../../models/exceptions/response-dto';
import { catchError, EMPTY, map, Observable, tap } from 'rxjs';
import { IPage } from '../../models/pagination/page';
import { MessageErrorResolver } from '../../utilities/message-error-resolver';
import { LoadingService } from './loading.service';
import { IListFederationDto } from '../../models/list-federation-dto';
import { toParams, toUrl } from '../../utilities/utility-functions';
import { FederationForm } from '@models/form/federation-post-form';
import { extractResponse } from '@shared/rxjs/custom-rxjs-operators';
import { FederationEventFilter } from '@models/federation/filters/federation-event-filter';
import { PageRequest } from '@models/pagination/page-request';
import { IFederationEventDto } from '@models/federation/dtos/federation-event-dto';

@Injectable({
  providedIn: 'root',
})
export class FederationService {
  private url: string = environment.apiUrl + '/v1/federacoes';

  constructor(
    private httpClient: HttpClient,
    private messageErrorResolver: MessageErrorResolver,
    private loadingService: LoadingService,
  ) {}

  public getAllFederations(
    searchText?: string,
    page?: number,
    size?: number,
  ): Observable<IPage<IListFederationDto>> {
    let params = new HttpParams();
    if (size) params = params.set('size', size);
    if (page) params = params.set('page', page);
    if (searchText) params = params.set('fedTxNome', searchText);

    return this.httpClient
      .get<ResponseDto<IPage<IListFederationDto>>>(this.url, { params })
      .pipe(
        map((response) => response.response),
        map((page) => {
          return {
            ...page,
            content: page.content?.map((federation) => {
              const sigla = federation.fedTxSigla;
              const nome = federation.fedTxNome;
              const fedTxNome = `${sigla} - ${nome}`;
              return {
                ...federation,
                fedTxNome,
              } as IListFederationDto;
            }),
          } as IPage<IListFederationDto>;
        }),
        catchError((error: ResponseWithErrorDto) => {
          this.loadingService.deactivate();
          this.messageErrorResolver.resolveMessage(error);
          return EMPTY;
        }),
      );
  }
  getDirectorUserFederation(
    page?: number,
    size?: number,
    fedTxNome?: string,
  ): Observable<IPage<IFederationDto>> {
    let params = new HttpParams();
    if (size) params = params.set('size', size);
    if (page) params = params.set('page', page);
    if (fedTxNome) params = params.set('fedTxNome', fedTxNome || '');
    return this.httpClient
      .get<
        ResponseDto<IPage<IFederationDto>>
      >(this.url + '/diretor', { params })
      .pipe(
        map((response) => response.response),
        map((page) => {
          return {
            ...page,
            content: page.content?.map((federation) => {
              const sigla = federation.fedTxSigla;
              const nome = federation.fedTxNome;
              const fedTxNome = `${sigla} - ${nome}`;
              return {
                ...federation,
                fedTxNome,
              } as IFederationDto;
            }),
          } as IPage<IFederationDto>;
        }),
        catchError((error: ResponseWithErrorDto) => {
          this.loadingService.deactivate();
          this.messageErrorResolver.resolveMessage(error);
          return EMPTY;
        }),
      );
  }
  getFederationById(id: number): Observable<IFederationDto> {
    this.loadingService.activate();
    let params = new HttpParams();
    params.set('idFederacao', id);
    return this.httpClient
      .get<ResponseDto<IFederationDto>>(this.url + `/${id}`, { params })
      .pipe(
        map((response) => response.response),
        map((federation) => ({
          ...federation,
          fedTxNome: `${federation.fedTxSigla} - ${federation.fedTxNome}`,
        })),
        catchError((err) => {
          this.loadingService.deactivate();
          this.messageErrorResolver.resolveMessage(err);
          throw EMPTY;
        }),
        tap(() => this.loadingService.deactivate()),
      );
  }
  getCbdeFederation() {
    return this.httpClient
      .get<ResponseDto<IFederationDto>>(this.url.concat('/cbde'))
      .pipe(
        map((response) => response.response),
        catchError((err) => {
          throw EMPTY;
        }),
      );
  }

  listFederationsByEventWithInstitutions$(
    filters?: FederationEventFilter,
    pageable?: PageRequest,
  ): Observable<IPage<IFederationEventDto>> {
    const params = toParams(filters, pageable);

    return this.httpClient
      .get<
        IResponseDto<IPage<IFederationEventDto>>
      >(toUrl(this.url, 'inscricoes-eventos'), { params })
      .pipe(extractResponse());
  }

  postFederation(form: FederationForm): Observable<void> {
    return this.httpClient.post<ResponseDto<void>>(this.url, form).pipe(
      extractResponse(),
      catchError((err) => {
        this.messageErrorResolver.resolveMessage(err);
        return EMPTY;
      }),
    );
  }
  putFederation(form: FederationForm): Observable<void> {
    return this.httpClient.put<ResponseDto<void>>(this.url, form).pipe(
      extractResponse(),
      catchError((err) => {
        this.messageErrorResolver.resolveMessage(err);
        return EMPTY;
      }),
    );
  }
  deleteFederation(fedNrId: number) {
    return this.httpClient
      .delete<ResponseDto<void>>(this.url + '/' + fedNrId)
      .pipe(
        catchError((err) => {
          this.messageErrorResolver.resolveMessage(err);
          return EMPTY;
        }),
      );
  }
}
